import { AnyAction, ID, MergedFlightWithContext } from "@nova/constants"
import { InfraredFlight, MergedFlight, MinMax, Range } from "@nova/protobuf"
import {
    selectHotspotFlightIdsForMergedFlightById,
    selectInfraredImagesAndUploadsForManyFlightIds,
} from "features/entity-caching/images/hotspots/selectors"
import { takeMapSnapshot } from "features/map/module"
import { zoomToShowFlightPath, zoomToShowHotspotsInFlightPath } from "features/map/zoom/thunks"
import {
    batchUpdateMergedFlight,
    updatedMergedFlight,
} from "features/pubsub/entityActions/cubes/mergedFlights"
import store from "integrations/redux/store"
import { debounce, get, set } from "nodash"
import { activateBaseHotspotLayers } from "panels/content/side-panel/thunks"
import { createAggregateSelector } from "./selectors"
import { selectInfraredFlightDataById } from "features/entity-caching/flights/selectors"
import { defaultHotspotProperties } from "@nova/serializer/src/client/validators/flights/infrared"
import { GenericRejectionResponseWithReason } from "integrations/redux/types"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { selectActivatedFireId } from "features/map/control/selectors"

export const zoomToMergedHotspots = (entity: MergedFlight) => {
    const state = store.getState()
    const dispatch = store.dispatch
    const flightIds = selectHotspotFlightIdsForMergedFlightById(entity.id)(state)
    const uploads = selectInfraredImagesAndUploadsForManyFlightIds(flightIds)(state)
    if (uploads && uploads.length > 0) {
        dispatch(zoomToShowFlightPath(uploads))
    } else dispatch(zoomToShowHotspotsInFlightPath({ flightId: entity.id }))
    dispatch(activateBaseHotspotLayers({ flightId: entity.id }))
    const projectId = selectActivatedFireId(state)!
    //we need to revisit when and how snapshots are being taken
    //updateMostRecentJobId()
    takeMapSnapshot(projectId)
}

export const toggleMergedFlight = (entity: MergedFlightWithContext) => {
    store.dispatch(toggleMergedFlightVisibility({ entity }))
}

export const toggleMergedFlightVisibility = createAsyncThunk<
    void,
    { entity: MergedFlightWithContext; visible?: boolean },
    GenericRejectionResponseWithReason
>("entities/mergedFlights/toggleVisibility", async (payload, thunkApi) => {
    const { entity, visible } = payload
    const dispatch = thunkApi.dispatch
    const isVisible = visible || entity.visible
    dispatch(updatedMergedFlight({ id: entity.id, context: {}, updates: { visible: !isVisible } }))
    dispatch(batchUpdateMergedFlight({ id: entity.id, updates: { visible: !isVisible } }))
})

export const shouldHideMergedFlightInspector = () => {
    return false
}

//a helper function that takes a dispatch action, a list of infrared flight ids, and then runs that action on all the ids
export const dispatchHotspotActionForAllMergedFlights = (dispatchAction: AnyAction, ids: ID[]) => {
    const dispatch = store.dispatch
    ids.forEach((id) => dispatch(dispatchAction({ flightId: id })))
}
export const dispatchHotspotUpdateActionForAllMergedFlights = (
    dispatchAction: AnyAction,
    ids: ID[],
    updates?: Record<
        string,
        | string
        | number
        | boolean
        | undefined
        | { range: Range[]; unit: string }
        | { perimeter?: boolean; temperature?: MinMax | undefined; size?: MinMax | undefined }
    >
) => {
    const dispatch = store.dispatch
    ids.forEach((id) => dispatch(dispatchAction({ id, updates })))
}

export const debouncedDispatchHotspotUpdateActionForAllMergedFlights = debounce(
    dispatchHotspotUpdateActionForAllMergedFlights,
    300
)

//determines what properties a merged flight should have
//the general rule is that if all the flights properties match, use those properties
//otherwise they reset to the defaults
export const getMergedFlightProperties = (flightIds: ID[]) => {
    const state = store.getState()
    const flights = createAggregateSelector(selectInfraredFlightDataById)(flightIds)(
        state
    ) as InfraredFlight[]

    const defaultProperties: Partial<InfraredFlight> = defaultHotspotProperties
    const properties: Record<string, string> = {}
    flights.forEach((flight) => {
        Object.entries(defaultProperties).forEach(([key, value]) => {
            // Leave scan areas unchanged at this point
            if (key === "scanAreaFillColor" || key === "scanAreaOutlineColor") {
                return
            }
            //if it's default, set the properties to the default
            if (get(flight, key) === value) {
                set(properties, key, value)
                return
            }
            //if the property hasnt been set yet or the property matches what we've set, then we want to keep using it
            if (!get(properties, key) || get(properties, key) === get(flight, key)) {
                set(properties, key, get(flight, key))
                return
            }
            //this flight has a property that doesn't match other properties in the merge, this field should be defaulted
            set(properties, key, value)
        })
    })
    return properties
}
